import { Injectable, NgZone } from '@angular/core';
import {
  debounceTime,
  fromEvent,
  merge,
  Observable,
  Subject,
  Subscription,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { SessionService } from 'src/app/core/auth/session.service';

@Injectable({
  providedIn: 'root',
})
export class InactivityService {
  private inactivityTime = 15 * 60 * 1000; // 15 minutos en milisegundos
  private inactivitySubject = new Subject<boolean>();
  private inactivitySubscription?: Subscription;
  private lastActivityTimestamp = Date.now();

  constructor(
    private readonly ngZone: NgZone,
    private readonly session: SessionService
  ) {
    this.session.setItem(
      'lastActivityTimestamp',
      this.lastActivityTimestamp.toString()
    );
  }

  get inactivity$(): Observable<boolean> {
    if (!this.inactivitySubscription) {
      this.startInactivityTimer();
    }
    return this.inactivitySubject.asObservable();
  }

  private startInactivityTimer() {
    this.ngZone.runOutsideAngular(() => {
      const events$ = merge(
        fromEvent(document, 'mousemove'),
        fromEvent(document, 'keydown'),
        fromEvent(document, 'click'),
        fromEvent(document, 'scroll'),
        fromEvent(document, 'touchstart')
      );

      events$
        .pipe(
          tap(() => this.resetTimer()),
          switchMap(() => timer(this.inactivityTime))
        )
        .subscribe(() => {
          this.ngZone.run(() => this.inactivitySubject.next(true));
        });

      events$.pipe(debounceTime(500)).subscribe(() => {
        this.lastActivityTimestamp = Date.now();

        const oldLastActivityTime = parseInt(
          this.session.getItem('lastActivityTimestamp') || '0'
        );

        const newLastActivitTime = this.lastActivityTimestamp;

        if (
          newLastActivitTime - oldLastActivityTime >=
          this.inactivityTime - 5000
        ) {
          this.inactivitySubject.next(true);

          return;
        }

        if (oldLastActivityTime)
          this.session.setItem(
            'lastActivityTimestamp',
            this.lastActivityTimestamp.toString()
          );
      });
    });
  }

  private resetTimer() {
    this.inactivitySubject.next(false);
  }

  public stopInactivityTimer() {
    if (this.inactivitySubscription) {
      this.inactivitySubscription.unsubscribe();
      this.inactivitySubscription = undefined;
    }
  }
}
